import { format } from 'date-fns/format';
import { observer } from 'mobx-react-lite';
import { type ComponentProps, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { ConfirmDialog } from '@/components/modals/ConfirmationModal';
import { ModalsManager } from '@/components/modals/modalsManager';
import { Button } from '@/components/ui';
import { Badge } from '@/components/ui/Badge';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

import { CancelSubscriptionModal } from './CancelSubscriptionModal';
import { AppStorePlanNotice, CalloutBox } from './subscription-banners';
import { PlanSelector } from './subscription-plans';
import { ActivePlanCard, UpgradePromptCard } from './subscription-status';
import { subscriptionApis, useSubscription } from './subscription.query';
import { subscriptionStore } from './subscription.store';
import { formatBillingDate, SubscriptionPlan } from './subscription.types';
import {
  type PowerOfferLabel,
  usePowerOfferLabel,
} from './use-power-offer-label';

function PromoCodeInput() {
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleApply = async () => {
    if (!code.trim()) return;
    setIsLoading(true);
    try {
      const result = await subscriptionApis.applyPromoCode(code.trim());
      if (result?.success) {
        setCode('');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <XStack className="items-center justify-between px-5 py-4 rounded-xl bg-surface border border-border dark:border-border-strong">
      <YStack className="gap-1 flex-1 mr-4">
        <span className="text-sm font-medium text-foreground">
          Have a promo code?
        </span>
        <XStack className="items-center gap-2 mt-1">
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleApply();
            }}
            placeholder="Enter code"
            disabled={isLoading}
            className="flex-1 max-w-[200px] h-8 px-3 text-sm rounded-md border border-border bg-background text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
          />
          <Button
            size="sm"
            variant="outline"
            onClick={handleApply}
            isLoading={isLoading}
            disabled={isLoading || !code.trim()}
          >
            Apply
          </Button>
        </XStack>
      </YStack>
    </XStack>
  );
}

function PowerOfferCard({ offer }: { offer: PowerOfferLabel }) {
  const [interval, setInterval] = useState<'monthly' | 'annual'>('monthly');
  const [isUpgrading, setIsUpgrading] = useState(false);

  const selected = offer.options.find((option) => option.interval === interval);

  const handleUpgrade = async () => {
    if (!selected) return;
    const confirmed = await ModalsManager.showResolvable(ConfirmDialog, {
      title: `Upgrade to Power (${selected.name})?`,
      description: selected.confirmDescription,
      confirmText: 'Confirm upgrade',
      confirmVariant: 'default',
      testId: 'power-upgrade-confirm',
    });
    if (!confirmed) return;
    setIsUpgrading(true);
    try {
      await subscriptionApis.upgradeToPower({ billing_interval: interval });
    } finally {
      setIsUpgrading(false);
    }
  };

  return (
    <YStack className="rounded-xl border border-border dark:border-border-strong bg-surface p-5 gap-4">
      <YStack className="gap-1">
        <XStack className="items-center justify-between">
          <span className="text-base font-medium text-foreground">
            {offer.ctaLabel}
          </span>
          <Badge variant="success-subtle" size="xs">
            Limited-time offer
          </Badge>
        </XStack>
        <span className="text-sm text-foreground-muted">
          Offer ends {offer.expiryLabel}
        </span>
      </YStack>
      <XStack className="gap-3">
        {offer.options.map((option) => (
          <button
            key={option.interval}
            type="button"
            aria-pressed={interval === option.interval}
            disabled={isUpgrading}
            onClick={() => setInterval(option.interval)}
            className={cn(
              'flex-1 rounded-lg border p-3 text-left transition-colors disabled:opacity-50',
              interval === option.interval
                ? 'border-accent bg-surface-active'
                : 'border-border-strong hover:border-border-strongest'
            )}
          >
            <YStack className="gap-1">
              <span className="text-sm font-medium text-foreground">
                {option.name}
              </span>
              <span className="text-sm text-foreground">
                {option.discountLabel}
              </span>
            </YStack>
          </button>
        ))}
      </XStack>
      <Button
        onClick={handleUpgrade}
        isLoading={isUpgrading}
        disabled={isUpgrading}
      >
        {offer.ctaLabel}
      </Button>
    </YStack>
  );
}

function AccountCreditBanner({ creditCents }: { creditCents: number }) {
  const isSubscribed = subscriptionStore.isActive;
  return (
    <XStack className="items-center gap-2 px-4 py-3 border border-green-600/30 bg-green-50 dark:bg-green-950/20 rounded-xl text-sm text-green-700 dark:text-green-400">
      <Icons.wallet className="w-4 h-4 shrink-0" />
      <span>
        You have{' '}
        <span className="font-medium">${(creditCents / 100).toFixed(2)}</span>{' '}
        in account credit
        {isSubscribed
          ? ' — this will be automatically applied to your upcoming invoices'
          : ' — this will be automatically applied when you subscribe'}
      </span>
    </XStack>
  );
}

type SubscriptionProps = ComponentProps<typeof PlanSelector>;

export const Subscription = observer((props: SubscriptionProps) => {
  const subscription = useSubscription();
  const accountCreditCents = subscriptionStore.state.accountCreditCents;
  const powerOfferLabel = usePowerOfferLabel();
  const isOnPlusPlan =
    subscriptionStore.state.currentPlan === SubscriptionPlan.PLUS_MONTHLY ||
    subscriptionStore.state.currentPlan === SubscriptionPlan.PLUS_ANNUAL;

  if (subscription.isLoading) {
    return (
      <YStack className="space-y-4">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-4 w-96" />
        <Skeleton className="h-32 w-full rounded-lg" />
      </YStack>
    );
  }

  const creditBanner = !!accountCreditCents && accountCreditCents > 0 && (
    <AccountCreditBanner creditCents={accountCreditCents} />
  );

  if (
    subscriptionStore.isActive ||
    subscription.status.subscription_status === 'past_due'
  ) {
    const isPastDue = subscription.status.subscription_status === 'past_due';
    const nextBillingDate = formatBillingDate(
      subscription.status.current_period_end
    );
    const billingInterval = subscriptionStore.billingInterval;
    const paymentMethod = subscription.status.payment_method;

    if (subscriptionStore.isAppStoreSubscriber) {
      return (
        <YStack className="bg-surface border border-border dark:border-border-strong rounded-xl py-4 px-6 gap-4">
          <AppStorePlanNotice copyClassName="text-sm text-foreground-muted" />
        </YStack>
      );
    }

    const handleManageSubscription = async () => {
      await subscription.createPortalSession();
    };

    const handleUpgrade = async () => {
      await subscription.createPortalSession({ is_upgrade: true });
    };

    const handleAddCard = async () => {
      await subscription.createPortalSession({ add_card: true });
    };

    const noCardTrialDaysLeft = subscriptionStore.noCardTrialDaysLeft;
    const trialEnd = subscription.status.current_period_end;

    const handleCancelSubscription = () => {
      ModalsManager.hideAll();
      ModalsManager.show(CancelSubscriptionModal);
    };

    const handleResumeSubscription = () => {
      subscription.resumeSubscription();
    };

    return (
      <YStack className="gap-4">
        {creditBanner}

        {isPastDue && (
          <CalloutBox
            variant="danger"
            title="Payment Failed"
            description="Update your payment method to avoid service interruption."
          />
        )}

        {subscriptionStore.cancelAtPeriodEnd && (
          <CalloutBox
            variant="warning"
            title="Cancellation Scheduled"
            description={
              <>
                Your subscription ends on{' '}
                <span className="font-medium">
                  {nextBillingDate || 'your next billing date'}
                </span>
                . You'll have access to all features until then.
              </>
            }
            action={
              <Button
                variant="outline"
                className="bg-accent text-accent-foreground hover:bg-accent-hover"
                size="sm"
                onClick={handleResumeSubscription}
                isLoading={subscription.isResuming}
                disabled={subscription.isResuming}
              >
                Don't Cancel
              </Button>
            }
          />
        )}

        {isOnPlusPlan && powerOfferLabel && (
          <PowerOfferCard offer={powerOfferLabel} />
        )}

        <ActivePlanCard
          planDisplayName={subscriptionStore.currentPlanLabel}
          billingInterval={billingInterval}
          nextBillingDate={nextBillingDate}
          paymentMethod={
            paymentMethod
              ? { brand: paymentMethod.brand, last4: paymentMethod.last4 }
              : null
          }
          cancelAtPeriodEnd={subscriptionStore.cancelAtPeriodEnd}
          noCardTrial={
            noCardTrialDaysLeft !== null && trialEnd
              ? {
                  endsOn: format(new Date(trialEnd * 1000), 'MMMM d'),
                  daysLeft: noCardTrialDaysLeft,
                }
              : null
          }
          onCancel={handleCancelSubscription}
          onManage={handleManageSubscription}
          onAddCard={handleAddCard}
          isCancelling={subscription.isCancelling}
          isManaging={subscription.isCreatingPortal}
          isDisabled={
            subscription.isCancelling ||
            subscription.isCreatingPortal ||
            subscription.isCreatingUpgrade
          }
        />

        {subscriptionStore.canUpgrade && (
          <UpgradePromptCard
            onUpgrade={handleUpgrade}
            isUpgrading={subscription.isCreatingUpgrade}
            isDisabled={
              subscription.isCancelling ||
              subscription.isCreatingPortal ||
              subscription.isCreatingUpgrade
            }
          />
        )}

        <PromoCodeInput />
      </YStack>
    );
  }

  return (
    <YStack className="gap-4">
      {creditBanner}
      <PlanSelector {...props} />
    </YStack>
  );
});
